/**
 * React hook for reading and writing a setting from the registry.
 *
 * Returns [currentValue, setter] similar to useState.
 * Listens for `storage` events so multiple components stay in sync
 * when the same setting is changed elsewhere (e.g. another tab or
 * direct localStorage writes via `setSetting`).
 */

import { useState, useCallback, useEffect } from 'react';
import { getSetting, setSetting } from './registry';

/**
 * Use a setting from the registry.
 *
 * @param key The localStorage key (must match a key in SETTINGS_REGISTRY for defaults to apply)
 * @returns [currentValue, setter] — setter writes to localStorage and triggers re-render
 */
export function useSetting(key: string): [string, (value: string) => void] {
  const [value, setValue] = useState(() => getSetting(key));

  const update = useCallback(
    (newValue: string) => {
      setSetting(key, newValue);
      setValue(newValue);
      // Dispatch a storage event so other components using useSetting on the
      // same key re-read the value. The native storage event only fires in
      // OTHER tabs, so we dispatch manually for same-tab reactivity.
      window.dispatchEvent(new Event('storage'));
    },
    [key],
  );

  // Listen for storage events (cross-tab + manual dispatches from same tab)
  useEffect(() => {
    const onStorage = () => {
      setValue(getSetting(key));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  return [value, update];
}
